/**
 * Erros da API, com a mensagem **real** do backend.
 *
 * Dívida técnica #50 (RN-627, RN-628): o legado exibia mensagem fixa e errada.
 * Aprovar um registro que falhava mostrava "Assessor já cadastrado!" fosse qual
 * fosse a causa, e excluir engolia o erro em silêncio — o usuário via a ação
 * "funcionar" e o dado continuar lá. Aqui a mensagem exibida é sempre a que o
 * backend mandou; quando não há mensagem, isso é dito explicitamente em vez de
 * inventarmos uma.
 *
 * O contrato de erro da API é `{ message, erros: [{ campo, mensagem }] }`
 * (`apps/api/src/common/zod-validation.pipe.ts`).
 */

export interface ErroDeCampo {
  readonly campo: string;
  readonly mensagem: string;
}

export class ErroApi extends Error {
  readonly status: number;
  readonly erros: readonly ErroDeCampo[];
  /** Corpo bruto, para depuração — nunca renderizado direto na tela. */
  readonly corpo: unknown;

  constructor(status: number, mensagem: string, erros: readonly ErroDeCampo[], corpo: unknown) {
    super(mensagem);
    this.name = 'ErroApi';
    this.status = status;
    this.erros = erros;
    this.corpo = corpo;
  }
}

/** Falha antes de haver resposta: rede caiu, CORS, servidor fora do ar. */
export class ErroDeRede extends Error {
  constructor(readonly causaOriginal: unknown) {
    super('Não foi possível falar com o servidor. Verifique sua conexão e tente de novo.');
    this.name = 'ErroDeRede';
  }
}

/** Sessão encerrada por falha de renovação — quem exibe manda para o login. */
export class ErroDeSessao extends Error {
  constructor(mensagem = 'Sua sessão expirou. Entre novamente.') {
    super(mensagem);
    this.name = 'ErroDeSessao';
  }
}

interface CorpoDeErro {
  message?: unknown;
  erros?: unknown;
}

/** Constrói o `ErroApi` a partir do corpo da resposta, sem supor formato. */
export function erroDaResposta(status: number, corpo: unknown): ErroApi {
  const objeto: CorpoDeErro = typeof corpo === 'object' && corpo !== null ? corpo : {};

  return new ErroApi(
    status,
    mensagemDoCorpo(status, objeto.message),
    camposDoCorpo(objeto.erros),
    corpo,
  );
}

function mensagemDoCorpo(status: number, message: unknown): string {
  if (typeof message === 'string' && message.trim() !== '') {
    // Único texto do backend que traduzimos: o 429 do throttler chega como
    // "ThrottlerException: Too Many Requests", que não diz nada a quem opera.
    // A causa é preservada — só a redação muda.
    if (status === 429 && message.startsWith('ThrottlerException')) {
      return 'Muitas tentativas em pouco tempo. Aguarde um minuto e tente de novo.';
    }
    return message;
  }

  if (Array.isArray(message)) {
    const textos = message.filter((m): m is string => typeof m === 'string');
    if (textos.length > 0) return textos.join(' · ');
  }

  return `O servidor respondeu ${status} sem mensagem. Registre o horário ao acionar o suporte.`;
}

function camposDoCorpo(erros: unknown): ErroDeCampo[] {
  if (!Array.isArray(erros)) return [];

  return erros.flatMap((item): ErroDeCampo[] => {
    if (typeof item !== 'object' || item === null) return [];
    const { campo, mensagem } = item as { campo?: unknown; mensagem?: unknown };
    if (typeof campo !== 'string' || typeof mensagem !== 'string') return [];
    return [{ campo, mensagem }];
  });
}

/** Texto para o usuário. Vale para qualquer `unknown` vindo de um `catch`. */
export function mensagemDoErro(erro: unknown): string {
  if (erro instanceof ErroApi || erro instanceof ErroDeRede || erro instanceof ErroDeSessao) {
    return erro.message;
  }
  if (erro instanceof Error && erro.message.trim() !== '') return erro.message;
  return 'Ocorreu um erro inesperado.';
}

/** Erros por campo, no formato que o formulário consome. */
export function errosPorCampo(erro: unknown): Record<string, string> {
  if (!(erro instanceof ErroApi)) return {};

  const mapa: Record<string, string> = {};
  for (const item of erro.erros) {
    // O primeiro problema de cada campo basta na tela; os demais reaparecem
    // quando o primeiro for corrigido.
    mapa[item.campo] ??= item.mensagem;
  }
  return mapa;
}
